import { jsx as _jsx } from "react/jsx-runtime";
import { useLocationPathname, useNavigate } from '@rocketlane/rocket-router';
import { createContext, useCallback, useContext, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { AccountFeature, useAccountFeature } from 'hooks/use-account-feature';
import { ROUTE_PATHS } from 'routes/routes-paths';
import { DEFAULT_APP_SHELL_MODE, isNitroClawPathname, persistAppShellMode, resolveAndPinAppShellModeSync } from 'utils/app-shell-mode';
import { isTauriApp } from 'utils/is-tauri';
const NITRO_CLAW_HOME = `/${ROUTE_PATHS.NITRO_CLAW}`;
const AppShellContext = /*#__PURE__*/ createContext(null);
const noopSetMode = (_mode)=>{};
const noopSwitchToAppShell = (_next)=>{};
const noopGetShellMode = ()=>DEFAULT_APP_SHELL_MODE;
/** Safe inactive shell when context is missing — never throw. */ const MISSING_PROVIDER_FALLBACK_APP_SHELL = {
    mode: DEFAULT_APP_SHELL_MODE,
    getShellMode: noopGetShellMode,
    setMode: noopSetMode,
    switchToAppShell: noopSwitchToAppShell,
    isNitroClawShellActive: false,
    isNitroClawEnabled: false,
    isShellReady: true
};
export const AppShellProvider = ({ children })=>{
    const navigate = useNavigate();
    const pathname = useLocationPathname();
    const [isNitroClawEnabled] = useAccountFeature(AccountFeature.NitroClaw);
    const [mode, setModeState] = useState(null);
    /** Single sync owner for chrome (switches, redirects, URL heals). */ const modeRef = useRef(DEFAULT_APP_SHELL_MODE);
    const hasPinnedRef = useRef(false);
    const prevPathnameRef = useRef(pathname);
    const setMode = useCallback((next)=>{
        modeRef.current = next;
        setModeState(next);
        persistAppShellMode(next);
    }, []);
    const getShellMode = useCallback(()=>modeRef.current, []);
    const isShellReady = mode != null;
    // Boot pin once, then heal only when pathname *enters* /nitro-claw.
    // Do not depend on `mode` — leave-claw sets mode first and must not be
    // overwritten while the path briefly lags.
    useLayoutEffect(()=>{
        if (!hasPinnedRef.current) {
            hasPinnedRef.current = true;
            const pinned = resolveAndPinAppShellModeSync(pathname);
            modeRef.current = pinned;
            setModeState(pinned);
            prevPathnameRef.current = pathname;
            return;
        }
        const prevPathname = prevPathnameRef.current;
        prevPathnameRef.current = pathname;
        if (isTauriApp() || !isNitroClawEnabled) {
            return;
        }
        const enteredClaw = !isNitroClawPathname(prevPathname) && isNitroClawPathname(pathname);
        if (!enteredClaw || modeRef.current === 'nitro-claw') {
            return;
        }
        setMode('nitro-claw');
    }, [
        pathname,
        isNitroClawEnabled,
        setMode
    ]);
    // Clear a claw preference when the feature is off — but not while still on a
    // claw URL. Chrome is already gated by `isNitroClawEnabled`; keeping the pin
    // lets a late flag hydrate reactivate claw without a path-edge heal.
    useEffect(()=>{
        if (mode == null || isTauriApp() || isNitroClawEnabled) {
            return;
        }
        if (mode !== 'nitro-claw' || isNitroClawPathname(pathname)) {
            return;
        }
        setMode(DEFAULT_APP_SHELL_MODE);
    }, [
        mode,
        isNitroClawEnabled,
        pathname,
        setMode
    ]);
    const switchToAppShell = useCallback((next)=>{
        // Mode first (sync ref + memory/session + state), then navigate.
        setMode(next);
        if (next === 'nitro-claw') {
            navigate({
                pathname: NITRO_CLAW_HOME,
                search: '',
                hash: ''
            });
            return;
        }
        // Leave claw-native URLs so home-redirect can't bounce chrome back.
        if (isNitroClawPathname(pathname)) {
            navigate({
                pathname: ROUTE_PATHS.ROOT,
                search: '',
                hash: ''
            });
        }
    }, [
        navigate,
        pathname,
        setMode
    ]);
    const isNitroClawShellActive = isShellReady && isNitroClawEnabled && (isTauriApp() || mode === 'nitro-claw');
    const value = useMemo(()=>({
            mode: mode !== null && mode !== void 0 ? mode : DEFAULT_APP_SHELL_MODE,
            getShellMode,
            setMode,
            switchToAppShell,
            isNitroClawShellActive,
            isNitroClawEnabled,
            isShellReady
        }), [
        mode,
        getShellMode,
        setMode,
        switchToAppShell,
        isNitroClawShellActive,
        isNitroClawEnabled,
        isShellReady
    ]);
    return /*#__PURE__*/ _jsx(AppShellContext.Provider, {
        value: value,
        children: children
    });
};
export const useAppShellContext = ()=>{
    const value = useContext(AppShellContext);
    if (value == null) {
        return MISSING_PROVIDER_FALLBACK_APP_SHELL;
    }
    return value;
};
